export type ChessColor = 'w' | 'b';
export type ChessPieceType = 'p' | 'n' | 'b' | 'r' | 'q' | 'k';
export type ChessPromotion = 'q' | 'r' | 'b' | 'n';

/** What decided a chess game on the board. timeout_insufficient: time ran out, but the opponent could not mate. */
export type ChessEnd = 'checkmate' | 'stalemate' | 'repetition' | 'fifty_moves' | 'insufficient_material' | 'timeout_insufficient';

/** A move as sent by the UI (squares like "e2"). */
export interface ChessMovePayload {
  from: string;
  to: string;
  promotion?: ChessPromotion;
}

/** Game-specific part of the chess state for one player. The whole position is public. */
export interface ChessView {
  /** Position in FEN. */
  fen: string;
  myColor: ChessColor | null;
  toMove: ChessColor;
  /** Moves so far in standard notation (SAN), white first. */
  history: string[];
  lastMove: { from: string; to: string } | null;
  inCheck: boolean;
  /** The viewer's legal moves on their turn: from-square → target squares. */
  legal: Record<string, string[]>;
}

export interface ChessPiece {
  color: ChessColor;
  type: ChessPieceType;
}

export const FILES = 'abcdefgh';

/** The board from a FEN: 64 squares from a8 to h1 (row by row, rank 8 first). */
export function boardFromFen(fen: string): (ChessPiece | null)[] {
  const squares: (ChessPiece | null)[] = [];
  for (const ch of fen.split(' ')[0] ?? '') {
    if (ch === '/') continue;
    if (/[1-8]/.test(ch)) {
      for (let i = 0; i < Number(ch); i++) squares.push(null);
    } else {
      const lower = ch.toLowerCase() as ChessPieceType;
      squares.push({ color: ch === lower ? 'b' : 'w', type: lower });
    }
  }
  return squares;
}

/** Square name of a board index (0 = a8 … 63 = h1). */
export const squareName = (index: number) => `${FILES[index % 8]}${8 - Math.floor(index / 8)}`;

const VALUES: Record<ChessPieceType, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
const START: Record<ChessPieceType, number> = { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 };

/** Pieces each side has captured (missing from the opponent) and the material difference for white. */
export function capturedPieces(board: (ChessPiece | null)[]): { byWhite: ChessPieceType[]; byBlack: ChessPieceType[]; whiteLead: number } {
  const count = { w: { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 }, b: { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 } };
  for (const p of board) if (p) count[p.color][p.type]++;
  const missing = (color: ChessColor) => {
    const out: ChessPieceType[] = [];
    for (const type of ['q', 'r', 'b', 'n', 'p'] as const) {
      // Promotions can make a count exceed the start; nothing is then missing.
      for (let i = count[color][type]; i < START[type]; i++) out.push(type);
    }
    return out;
  };
  const material = (color: ChessColor) => (Object.keys(VALUES) as ChessPieceType[]).reduce((n, t) => n + VALUES[t] * count[color][t], 0);
  return { byWhite: missing('b'), byBlack: missing('w'), whiteLead: material('w') - material('b') };
}
